import React, { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Check, RotateCw } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const SIZE = 320; // on-screen crop viewport (px)
const OUT = 800;  // exported square size (px)

export function PhotoCropDialog({ file, onCancel, onCropped }) {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => { setImg(i); setZoom(1); setRot(0); setPos({ x: 0, y: 0 }); };
    i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const base = img ? Math.max(SIZE / img.width, SIZE / img.height) : 1; // cover
  const draw = (ctx, scaleTo) => {
    const k = scaleTo / SIZE;
    ctx.clearRect(0, 0, scaleTo, scaleTo);
    ctx.save();
    ctx.translate(scaleTo / 2 + pos.x * k, scaleTo / 2 + pos.y * k);
    ctx.rotate((rot * Math.PI) / 180);
    const s = base * zoom * k;
    ctx.drawImage(img, (-img.width * s) / 2, (-img.height * s) / 2, img.width * s, img.height * s);
    ctx.restore();
  };
  useEffect(() => { if (img && canvasRef.current) draw(canvasRef.current.getContext("2d"), SIZE); });

  const start = (e) => { const p = e.touches ? e.touches[0] : e; drag.current = { x: p.clientX - pos.x, y: p.clientY - pos.y }; };
  const move = (e) => { if (!drag.current) return; const p = e.touches ? e.touches[0] : e; setPos({ x: p.clientX - drag.current.x, y: p.clientY - drag.current.y }); };
  const end = () => { drag.current = null; };

  const confirm = () => {
    const c = document.createElement("canvas"); c.width = OUT; c.height = OUT;
    draw(c.getContext("2d"), OUT);
    c.toBlob((blob) => onCropped(new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" })), "image/jpeg", 0.9);
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="photo-crop-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-crimson">Crop & centre photo</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Drag to position the face inside the circle. Use the slider to zoom.</p>
        <div className="mx-auto select-none" style={{ width: SIZE, height: SIZE, position: "relative", cursor: "grab", touchAction: "none" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="photo-crop-canvas">
          <canvas ref={canvasRef} width={SIZE} height={SIZE} className="rounded-xl bg-stone-200" />
          <div className="pointer-events-none absolute inset-0 rounded-xl" style={{ background: "radial-gradient(circle at center, transparent 49.5%, rgba(122,12,12,0.55) 50%)" }} />
          <div className="pointer-events-none absolute inset-0 rounded-full border-2 border-gold/90" />
        </div>
        <div className="flex items-center gap-3">
          <ZoomOut size={16} className="text-muted-foreground" />
          <input type="range" min="1" max="4" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-crimson" data-testid="photo-crop-zoom" />
          <ZoomIn size={16} className="text-muted-foreground" />
          <Button size="icon" variant="ghost" title="Rotate 90°" onClick={() => setRot((r) => (r + 90) % 360)} data-testid="photo-crop-rotate"><RotateCw size={16} /></Button>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="photo-crop-cancel">Cancel</Button>
          <Button className="bg-crimson" onClick={confirm} disabled={!img} data-testid="photo-crop-confirm"><Check size={16} className="mr-1" /> Use this photo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
